<script lang="ts" module>
    import { cn } from '#lib/utils.ts';
    import {
        Button as Upstream,
        buttonVariants as shadcnButtonVariants,
        type ButtonProps as ShadcnButtonProps
    } from '#lib/components/ui-shadcn-svelte/button/index.ts';
    import { type VariantProps, tv } from 'tailwind-variants';
    import type { TooltipParameter } from '#lib/actions/tooltip.ts';

    export const buttonVariants = tv({
        extend: shadcnButtonVariants,
        variants: {
            variant: {
                // Upstream's color-mix hover reads --secondary as a color; ours is a bare HSL triplet.
                secondary: 'hover:bg-secondary/80',
                icon: 'text-muted-foreground normal-case',
                mutedGhost:
                    'text-muted-foreground hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
                primary: 'bg-primary text-background hover:ring-1 hover:ring-primary/90 hover:bg-primary/90',
                primaryMuted:
                    'bg-primary-foreground text-primary border-primary-background hover:bg-primary-foreground/50 hover:ring-1 hover:ring-primary',
                primaryOutline:
                    'border border-primary bg-transparent text-primary hover:bg-primary hover:text-background',
                error: 'bg-destructive text-background hover:ring-1 hover:ring-destructive/90 hover:bg-destructive/90',
                errorMuted:
                    'bg-destructive-foreground text-destructive border-destructive-background hover:bg-destructive-foreground/50 hover:ring-1 hover:ring-destructive',
                errorOutline:
                    'border border-destructive bg-transparent text-destructive hover:bg-destructive hover:text-background',
                success: 'bg-success text-background hover:ring-1 hover:ring-success/90 hover:bg-success/90',
                successMuted:
                    'bg-success-foreground text-success border-success-background hover:bg-success-foreground/50 hover:ring-1 hover:ring-success',
                successOutline:
                    'border border-success bg-transparent text-success hover:bg-success hover:text-background',
                info: 'bg-info text-background hover:ring-1 hover:ring-info/90 hover:bg-info/90',
                infoMuted:
                    'bg-info-foreground text-info border-info-background hover:bg-info-foreground/50 hover:ring-1 hover:ring-info',
                infoOutline: 'border border-info bg-transparent text-info hover:bg-info hover:text-background',
                warning: 'bg-warning text-background hover:ring-1 hover:ring-warning/90 hover:bg-warning/90',
                warningMuted:
                    'bg-warning-foreground text-warning border-warning-background hover:bg-warning-foreground/50 hover:ring-1 hover:ring-warning',
                warningOutline:
                    'border border-warning bg-transparent text-warning hover:bg-warning hover:text-background',
                muted: 'bg-muted text-muted-foreground hover:bg-muted/90 hover:ring-1 hover:ring-muted-foreground',
                mutedOutline:
                    'border border-muted-foreground bg-transparent text-muted-foreground hover:bg-muted-foreground hover:text-background',
                mutedDark:
                    'bg-muted-foreground/90 text-background hover:bg-muted-foreground/90  hover:ring-1 hover:bg-muted-foreground/90'
            },
            size: {
                default: 'h-9 px-4 py-2 has-[>svg]:px-3',
                sm: 'h-8 gap-1.5 rounded-md px-3 has-[>svg]:px-2.5',
                lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
                icon: "size-6 border-0 p-0 leading-none [&_svg:not([class*='size-'])]:size-6",
                'icon-sm': "size-5 border-0 p-0 leading-none [&_svg:not([class*='size-'])]:size-5",
                'icon-lg': "size-8 border-0 p-0 leading-none [&_svg:not([class*='size-'])]:size-6",
                iconOutline: 'h-10 p-2',
                wide: 'h-10 min-w-40 px-4 py-2'
            }
        }
    });

    export type ButtonVariant = VariantProps<typeof buttonVariants>['variant'];
    export type ButtonSize = VariantProps<typeof buttonVariants>['size'];

    export type ButtonProps = Omit<ShadcnButtonProps, 'variant' | 'size'> & {
        variant?: ButtonVariant;
        size?: ButtonSize;
        tooltip?: TooltipParameter;
    };
</script>

<script lang="ts">
    import { tooltip as tooltipAction } from '#lib/actions/tooltip.ts';

    let {
        class: className,
        variant = 'default',
        size = 'default',
        tooltip,
        ref = $bindable(null),
        ...restProps
    }: ButtonProps = $props();

    // The action can't be applied through the upstream component, so bind it to the rendered element.
    // `tooltip` is read first so buttons without one never take a dependency on `ref`.
    $effect(() => {
        if (!tooltip) return;
        if (!ref) return;

        return tooltipAction(ref, tooltip)?.destroy;
    });
</script>

<!--
    Concord variant/size values aren't in the base button's union, so they're cast
    when passed through. Passing them (rather than leaving them undefined) stops the
    base component falling back to its own defaultVariants: it looks ours up, finds
    nothing, and emits only its base classes — our extended classes do the styling.
-->
<Upstream
    bind:ref
    variant={variant as ShadcnButtonProps['variant']}
    size={size as ShadcnButtonProps['size']}
    class={cn(buttonVariants({ variant, size }), className)}
    {...restProps} />
